import { CRYSTALLIZE_IDS } from "../../crystallize-constants.js";
import Logger from "@cappelendamm/logger";
import { sortDate } from "../../utils.js";
import { getBookFromProductVariant } from "../../product/selectors/getBookFromProductVariant.js";
import { getComponentContentById } from "@cappelendamm/crystallize-utils";
import { getSubscriptionType } from "./getSubscriptionType.js";
import { isSubscriptionShapeBookClub } from "../../subscription-plan/selectors/isSubscriptionShape.js";
const sortBooksByName = (books) => [...books].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
const getProductsFromItemRelation = (components, componentId) => {
    const relation = getComponentContentById(components, componentId);
    const fromProductVariants = (relation?.productVariants ?? []).map((variant) => getBookFromProductVariant(variant));
    const fromItems = (relation?.items ?? []).flatMap((item) => {
        const productItem = item;
        if (!productItem.defaultVariant?.sku) {
            return [];
        }
        const variant = {
            ...productItem.defaultVariant,
            product: {
                shape: productItem.shape,
            },
        };
        return [getBookFromProductVariant(variant)];
    });
    const products = [...fromProductVariants, ...fromItems].filter((product, index, list) => list.findIndex((candidate) => candidate.sku === product.sku) === index);
    if (products.length === 0) {
        return [];
    }
    return products;
};
const getBookClubDispatchProducts = (dispatchChunkComponents) => {
    const ids = CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB.DISTRIBUTION_DATES;
    // Find the piece component directly since PieceContent is not a standard content type
    const bookClubPieceComponent = dispatchChunkComponents?.find((comp) => comp.componentId === ids.BOOK_CLUB_PRODUCT_PIECE);
    const bookClubPiece = bookClubPieceComponent?.content;
    const productChunks = getComponentContentById(bookClubPiece?.components, ids.PRODUCTS_CHUNK.ID)?.chunks;
    if (!productChunks || productChunks.length === 0) {
        return [];
    }
    const products = productChunks.flatMap((productChunk) => getProductsFromItemRelation(productChunk, ids.PRODUCTS_CHUNK.PRODUCT));
    return sortBooksByName(products);
};
const getBookClubDispatchPresentation = (dispatchChunkComponents) => {
    const ids = CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB.DISTRIBUTION_DATES.PRESENTATION;
    const getText = (componentId) => getComponentContentById(dispatchChunkComponents, componentId)?.text?.trim() || undefined;
    const html = (getComponentContentById(dispatchChunkComponents, ids.TEXT)
        ?.html ?? []).filter((block) => !!block?.trim());
    const presentation = {
        title: getText(ids.TITLE),
        href: getText(ids.LINK),
        imageUrl: getComponentContentById(dispatchChunkComponents, ids.IMAGE)
            ?.images?.[0]?.url ?? undefined,
        html: html.length > 0 ? html : undefined,
    };
    return Object.values(presentation).some(Boolean) ? presentation : undefined;
};
const getBookClubDispatchPrice = (dispatchChunkComponents, products) => {
    const ids = CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB.DISTRIBUTION_DATES;
    // Priority 1: explicit dispatch price on distribusjonsdatoer chunk
    const dispatchPrice = getComponentContentById(dispatchChunkComponents, ids.PRICE)?.number;
    if (dispatchPrice !== undefined) {
        return dispatchPrice;
    }
    // Priority 2: sum product prices in this dispatch.
    // Missing product prices are treated as 0.
    const list = products ?? [];
    if (list.length === 0) {
        return undefined;
    }
    return list.reduce((sum, product) => {
        const price = product.price;
        return (sum + (typeof price === "number" && !Number.isNaN(price) ? price : 0));
    }, 0);
};
export const getSubscriptionDispatches = (subscription) => {
    const subscriptionType = getSubscriptionType(subscription);
    const isBookClub = isSubscriptionShapeBookClub(subscriptionType);
    const subscriptionIds = isBookClub
        ? CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB
        : CRYSTALLIZE_IDS.SHAPE.PRODUCT.SUBSCRIPTION;
    const dispatchChunk = subscription.product.components?.find(({ componentId: id }) => id === subscriptionIds.DISTRIBUTION_DATES.ID)?.content;
    if (dispatchChunk?.__typename !== "ContentChunkContent") {
        Logger.log(`Dispatch on subscription sku: ${subscription.sku} is not of type ContentChunkContent`);
        return undefined;
    }
    const dispatches = dispatchChunk.chunks?.map((item) => {
        const products = isBookClub
            ? getBookClubDispatchProducts(item)
            : getProductsFromItemRelation(item, subscriptionIds.DISTRIBUTION_DATES.PRODUCT);
        const distributionDate = getComponentContentById(item, subscriptionIds.DISTRIBUTION_DATES.SEND_DATE)?.datetime;
        const skipBookDeadline = getComponentContentById(item, subscriptionIds.DISTRIBUTION_DATES.CANCELLATION_DEADLINE)?.datetime;
        const memberExtractionDate = isBookClub
            ? getComponentContentById(item, CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB.DISTRIBUTION_DATES
                .MEMBER_EXTRACTION_DATE)?.datetime
            : undefined;
        // Only read batch code for book club
        const batchCode = isBookClub
            ? (getComponentContentById(item, CRYSTALLIZE_IDS.SHAPE.PRODUCT.BOOK_CLUB.DISTRIBUTION_DATES.BATCH_CODE)?.text ?? undefined)
            : undefined;
        // Only read price and presentation for book club
        const price = isBookClub
            ? getBookClubDispatchPrice(item, products)
            : undefined;
        const presentation = isBookClub
            ? getBookClubDispatchPresentation(item)
            : undefined;
        return {
            products,
            distributionDate,
            skipBookDeadline,
            memberExtractionDate,
            batchCode,
            price,
            presentation,
        };
    });
    if (!dispatches || dispatches.length === 0) {
        Logger.log(`No dispatches found for subscription sku: ${subscription.sku}`);
        return undefined;
    }
    return dispatches.sort(({ distributionDate: a }, { distributionDate: b }) => sortDate(a, b));
};
